import { Children, isValidElement, useId, useState, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface CodeTabsProps {
  /** Tab labels in order, one per fenced code block child (e.g. ["cURL", "Python", "JavaScript"]). */
  labels: string[];
  children: ReactNode;
}

/**
 * Tabbed multi-language code block for MDX posts. Wrap N fenced code blocks (one
 * per language) and pass matching `labels`; it renders a tab bar and shows one
 * block at a time. Every block stays in the SSR HTML (inactive ones are just
 * hidden), so all languages are crawlable and there is no layout shift. Each
 * child keeps the hover copy button from the `pre` override.
 */
export const CodeTabs = ({ labels, children }: CodeTabsProps) => {
  const blocks = Children.toArray(children).filter(isValidElement);
  const [active, setActive] = useState(0);
  const id = useId();
  return (
    <div className="my-5 overflow-hidden rounded-10 border border-line">
      <div role="tablist" aria-label="Code language" className="flex gap-1 border-b border-line bg-surface-2 px-2 pt-1.5">
        {labels.map((label, i) => (
          <button
            key={label}
            type="button"
            role="tab"
            id={`${id}-tab-${i}`}
            aria-selected={i === active}
            onClick={() => setActive(i)}
            className={cn(
              'mono rounded-t-md px-3 py-1.5 text-12 transition-colors',
              i === active ? 'bg-bg text-text' : 'text-text-3 hover:text-text-2',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {blocks.map((block, i) => (
        <div
          key={i}
          role="tabpanel"
          aria-labelledby={`${id}-tab-${i}`}
          hidden={i !== active}
          className="[&>div]:my-0 [&_pre]:rounded-none [&_pre]:border-0"
        >
          {block}
        </div>
      ))}
    </div>
  );
};
